import React, { memo, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';

import { mapDispatchToOnClick, mapStateToOpen } from '../../containers/orders-drawer/sections/section-container-helper';
import { ComponentStates } from '../../enums/component-states';
import OtherShippingOptionConstants from '../../constants/other-shipping-options-constants';
import { useEnableShippingOptionsForRateShoppers } from '../../feature-flags/hooks/use-enable-shipping-options-for-rate-shoppers';
import { usePrimarySellerLabelProviderIdForService } from '../../hooks/use-primary-seller-label-provider-for-service';
import { OtherShippingOptions } from '../../orders/other-shipping-options/component';
import { confirmation, confirmationEnumId, movementIndicator } from '../../services/other-shipping-options/constants';
import { OtherShippingOptionsService } from '../../services/other-shipping-options/other-shipping-options-service';
import { CustomsReason } from '../../constants/customs';
import { useSellerFulfillmentProviderByExternalShipFromId } from '../../hooks/use-seller-fulfillment-provider';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';
import { emptyArray } from 'ui-components/default-props/empty';

const sectionId = 'otherShippingOptions';

const RATE_SHOPPER_ENABLED_OPTIONS = [
  OtherShippingOptionConstants.additionalHandling,
  OtherShippingOptionConstants.alcohol,
  OtherShippingOptionConstants.billToSender,
  OtherShippingOptionConstants.certificateOfOrigin,
  OtherShippingOptionConstants.dangerousGoodsSimple,
  OtherShippingOptionConstants.doNotNotifyMarketplace,
  OtherShippingOptionConstants.dryIce,
  OtherShippingOptionConstants.fedExBookingConfirmation,
  OtherShippingOptionConstants.fedExShipperLoadAndCount,
  OtherShippingOptionConstants.includeReturnLabel,
  OtherShippingOptionConstants.nonMachinable,
  OtherShippingOptionConstants.saturdayDelivery,
  OtherShippingOptionConstants.shipperRelease,
];

const fulfillmentProviderOptionsSelectorFactory = () =>
  createSelector(
    (sellerFulfillmentProvider) => sellerFulfillmentProvider,
    (_sellerFulfillmentProvider, allFulfillmentProviders) => allFulfillmentProviders,
    (_sellerFulfillmentProvider, _allFulfillmentProviders, allFulfillmentProviderOptions) => allFulfillmentProviderOptions,
    (sellerFulfillmentProvider, allFulfillmentProviders, allFulfillmentProviderOptions) => {
      if (!sellerFulfillmentProvider) {
        return emptyArray;
      }

      const apiCode = allFulfillmentProviders.find(
        (fp) => `${fp.fillProviderId}` === sellerFulfillmentProvider.fulfillmentProviderId,
      )?.apiCode;
      const integrationOptions = allFulfillmentProviderOptions?.find((option) => option.apiCode === apiCode)?.fulfillmentOptions;
      if (!integrationOptions?.length) {
        return emptyArray;
      }

      return integrationOptions.map((io) => io.code);
    },
  );

const enabledOptionsSelector = createSelector(
  (options) => options,
  (options) => {
    const enabledOptions = {};
    if (options !== null && options !== undefined) {
      Object.keys(options)
        .filter((opt) => opt !== confirmation || opt !== confirmationEnumId)
        .filter((opt) => options[opt])
        .forEach((opt) => (enabledOptions[opt] = options[opt]));
    }
    return enabledOptions;
  },
);

// Cached selector with multi-entry cache keyed by serviceCollectionGuid or serviceId
export const makeOptionsSelector = createCachedSelector(
  (enabledOptions) => enabledOptions,
  (_enabledOptions, serviceId) => serviceId,
  (_enabledOptions, _serviceId, sellerLabelProviderId) => sellerLabelProviderId,
  (_enabledOptions, _serviceId, _sellerLabelProviderId, sellerServices) => sellerServices,
  (_enabledOptions, _serviceId, _sellerLabelProviderId, _sellerServices, serviceCollectionGuid) => serviceCollectionGuid,
  (_enabledOptions, _serviceId, _sellerLabelProviderId, _sellerServices, _serviceCollectionGuid, serviceCollections) => serviceCollections,
  (_enabledOptions, _serviceId, _sellerLabelProviderId, _sellerServices, _serviceCollectionGuid, _serviceCollections, matchedCountryCode) =>
    matchedCountryCode,
  (
    _enabledOptions,
    _serviceId,
    _sellerLabelProviderId,
    _sellerServices,
    _serviceCollectionGuid,
    _serviceCollections,
    _matchedCountryCode,
    sellerDefaultCurrency,
  ) => sellerDefaultCurrency,
  (
    _enabledOptions,
    _serviceId,
    _sellerLabelProviderId,
    _sellerServices,
    _serviceCollectionGuid,
    _serviceCollections,
    _matchedCountryCode,
    _sellerDefaultCurrency,
    fulfillmentOptions,
  ) => fulfillmentOptions,
  (
    _enabledOptions,
    _serviceId,
    _sellerLabelProviderId,
    _sellerServices,
    _serviceCollectionGuid,
    _serviceCollections,
    _matchedCountryCode,
    _sellerDefaultCurrency,
    _fulfillmentOptions,
    fulfillmentProperties,
  ) => fulfillmentProperties,
  (
    _enabledOptions,
    _serviceId,
    _sellerLabelProviderId,
    _sellerServices,
    _serviceCollectionGuid,
    _serviceCollections,
    _matchedCountryCode,
    _sellerDefaultCurrency,
    _fulfillmentOptions,
    _fulfillmentProperties,
    isShippingOptionsForRateShoppersEnabled,
  ) => isShippingOptionsForRateShoppersEnabled,
  (
    enabledOptions,
    serviceId,
    sellerLabelProviderId,
    sellerServices,
    serviceCollectionGuid,
    serviceCollections,
    matchedCountryCode,
    sellerDefaultCurrency,
    fulfillmentOptions,
    fulfillmentProperties,
    isShippingOptionsForRateShoppersEnabled,
  ) => {
    let activeOptions = {};

    const isRatingServiceCollection = serviceCollectionGuid && serviceCollections.length;
    if (isRatingServiceCollection) {
      const collection = serviceCollections.find((c) => c.serviceCollectionGuid === serviceCollectionGuid);

      const isEligibleForExpandedOtherShippingOptions = collection?.isTemplate || isShippingOptionsForRateShoppersEnabled;

      if (isEligibleForExpandedOtherShippingOptions && collection?.services?.length) {
        const allOptionCodes = collection.services
          .map((collectionService) => {
            const matchedService = sellerServices.find((s) => `${s.serviceId}` === `${collectionService.shippingServiceId}`);
            return matchedService?.optionCodes ?? emptyArray;
          })
          .flat();

        const uniqueOptionCodes = [...new Set(allOptionCodes)];

        const whitelistedOptionCodes = uniqueOptionCodes.filter((code) => RATE_SHOPPER_ENABLED_OPTIONS.includes(code));

        activeOptions = enabledOptions || activeOptions;
        const optionCodes = whitelistedOptionCodes.concat(fulfillmentOptions).concat(OtherShippingOptionsService.getDefaultOptions());

        return OtherShippingOptionsService.buildOptions(
          optionCodes,
          activeOptions,
          matchedCountryCode,
          sellerDefaultCurrency,
          fulfillmentProperties,
        );
      }
    }

    if (serviceId && sellerServices.length !== 0) {
      const getFilteredServices = () => {
        if (sellerLabelProviderId) {
          return sellerServices.filter((service) => service.sellerLabelProviderId === sellerLabelProviderId);
        }
        return sellerServices;
      };
      const filteredServices = getFilteredServices();
      const service = filteredServices.find((s) => serviceId === s.serviceId);

      activeOptions = enabledOptions || activeOptions;
      const optionCodes = (service?.optionCodes || emptyArray)
        .concat(fulfillmentOptions)
        .concat(OtherShippingOptionsService.getDefaultOptions());
      return OtherShippingOptionsService.buildOptions(
        optionCodes,
        activeOptions,
        matchedCountryCode,
        sellerDefaultCurrency,
        fulfillmentProperties,
      );
    }

    const defaultOptions = fulfillmentOptions.concat(OtherShippingOptionsService.getDefaultOptions());

    return OtherShippingOptionsService.buildOptions(
      defaultOptions,
      enabledOptions,
      matchedCountryCode,
      sellerDefaultCurrency,
      fulfillmentProperties,
    );
  },
)(
  (_enabledOptions, serviceId, _sellerLabelProviderId, _sellerServices, serviceCollectionGuid) =>
    serviceCollectionGuid || serviceId || 'default',
);

/**
 * @param {object} props
 * @param {boolean} [props.isReadOnly]
 * @param {boolean} [props.open]
 * @param {() => void} [props.onClickHeader]
 * @returns {JSX.Element}
 */
const FulfillmentPlannerContextDrivenOtherShippingOptionsComponent = ({ isReadOnly = false, open, onClickHeader, ...rest }) => {
  const {
    fulfillmentPlanId,
    otherShippingOptions,
    onOtherShippingOptionsChange,
    serviceIdAndSellerLabelProviderId,
    serviceCollectionGuid,
    shipFromId,
    externalShipFromId,
    customsReason,
  } = useFulfillmentPlannerContext();

  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId })) || {};
  const { properties, recipientInfo } = fulfillmentPlan;

  const sellerDefaultCurrency = useSelector(Global.selectSellerDefaultCurrency);
  const sellerServices = useSelector(Global.selectSellerServices);
  const serviceCollections = useSelector(Global.selectSellerServiceCollections);
  const matchedCountryCode = useSelector((state) => {
    const shipFrom = Global.selectSellerShipFromsById(state, { shipFromId });
    return shipFrom?.countryCode ?? 'US';
  });

  const hideOtherShippingOptions = useSelector((state) => {
    const restrictions = Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'restrictions',
    });
    return restrictions?.labelConfiguration?.componentState === ComponentStates.Hidden;
  });

  const enabledOptions = enabledOptionsSelector(otherShippingOptions);
  const confirmedSellerLabelProviderId = usePrimarySellerLabelProviderIdForService(serviceIdAndSellerLabelProviderId.serviceId);
  const sellerFulfillmentProvider = useSellerFulfillmentProviderByExternalShipFromId(externalShipFromId);
  const allFulfillmentProviders = useSelector(Global.selectReferenceDatagetFulfillmentProviders) ?? [];
  const allFulfillmentProviderOptions = useSelector(Global.selectSellerFulfillmentOptions);
  const fulfillmentProviderOptionsSelector = useMemo(fulfillmentProviderOptionsSelectorFactory, []);
  const fulfillmentOptions = fulfillmentProviderOptionsSelector(
    sellerFulfillmentProvider,
    allFulfillmentProviders,
    allFulfillmentProviderOptions,
  );
  const isShippingOptionsForRateShoppersEnabled = useEnableShippingOptionsForRateShoppers();
  const options = makeOptionsSelector(
    enabledOptions,
    serviceIdAndSellerLabelProviderId.serviceId,
    serviceIdAndSellerLabelProviderId.sellerLabelProviderId || confirmedSellerLabelProviderId,
    sellerServices,
    serviceCollectionGuid,
    serviceCollections,
    matchedCountryCode,
    sellerDefaultCurrency,
    fulfillmentOptions,
    { properties, recipientInfo },
    isShippingOptionsForRateShoppersEnabled,
  );
  const optionsWithMovementIndicator = useMemo(() => {
    if (customsReason !== CustomsReason.RequireMovementIndicator || options[movementIndicator] !== undefined) {
      return options;
    }
    const savedValue = enabledOptions?.[movementIndicator] ?? null;
    return { ...options, [movementIndicator]: { checked: savedValue != null, value: savedValue } };
  }, [options, customsReason, enabledOptions]);

  const isOpen = useSelector((state) => {
    return typeof open === 'boolean' ? open : mapStateToOpen(state, sectionId).open;
  });

  const dispatch = useDispatch();

  const onClick = onClickHeader || mapDispatchToOnClick(dispatch, sectionId).onClick;

  return (
    <OtherShippingOptions
      editableFulfillmentPlanId={isReadOnly ? null : fulfillmentPlanId}
      fulfillmentPlanId={fulfillmentPlanId}
      hideOtherShippingOptions={hideOtherShippingOptions}
      onClick={onClick}
      open={isOpen}
      options={optionsWithMovementIndicator}
      saveOptions={onOtherShippingOptionsChange}
      serviceId={serviceIdAndSellerLabelProviderId.serviceId}
      {...rest}
    />
  );
};

FulfillmentPlannerContextDrivenOtherShippingOptionsComponent.propTypes = {
  isReadOnly: PropTypes.bool,
  open: PropTypes.bool,
  onClickHeader: PropTypes.func,
};

export const FulfillmentPlannerContextDrivenOtherShippingOptions = memo(FulfillmentPlannerContextDrivenOtherShippingOptionsComponent);
